gsap.registerPlugin(ScrollTrigger);

class PremiumWordTransition {

    constructor(selector = ".premium-transition") {

        this.section = document.querySelector(selector);

        if (!this.section) return;

        this.words = [...this.section.querySelectorAll(".transition-word")];

        if (!this.words.length) return;

        // Trigger on the text block itself, NOT the outer section. The section
        // often has large padding, so its top edge is far above the words —
        // measuring from it makes progress advance before the words are on
        // screen. The heading that actually contains the words is the correct
        // reference point, so the sweep begins exactly when word 1 appears.
        this.trigger = this.words[0].closest("h1, h2, h3, h4, .premium-heading")
            || this.words[0].parentElement
            || this.section;

        this.colors = {
            gray: "#4A4A4A",
            blue1: "#58A8FF",
            blue2: "#228CFF",
            white: "#FFFFFF"
        };

        // Width (in % of each word) of the blue gradient zone that rides between
        // the revealed white and the not-yet-revealed gray. Larger = the blue
        // spreads wider across the word as it transitions. Tune to taste.
        this.blueSpread = 65;

        this.init();

    }

    init() {
        this.prepareWords();
        this.createScroll();
    }

    prepareWords() {

        this.words.forEach(word => {

            word.style.color = this.colors.gray;
            word.style.background = "none";
            word.style.webkitTextFillColor = this.colors.gray;

            word.style.backgroundClip = "text";
            word.style.webkitBackgroundClip = "text";

            word.style.backgroundRepeat = "no-repeat";
            word.style.backgroundSize = "260% 100%";

        });

    }

    clamp(value, min = 0, max = 1) {
        return Math.max(min, Math.min(max, value));
    }

    lerp(start, end, progress) {
        return start + (end - start) * progress;
    }

    gradient(progress) {

        progress = this.clamp(progress);

        // Boundary sweeps LEFT -> RIGHT as progress goes 0 -> 1.
        // 90deg => 0% is the left edge of the word, 100% the right edge.
        // A WIDE blue zone (this.blueSpread) rides between the revealed white on
        // the left and the not-yet-revealed gray on the right: white -> light
        // blue -> deep blue -> gray. The wider the spread, the more of the word
        // glows blue while it transitions.
        const b = progress * 100;
        const half = this.blueSpread / 2;

        return `linear-gradient( 90deg, ${this.colors.white} 0%, ${this.colors.white} ${b - half}%, ${this.colors.blue1} ${b - half * 0.45}%, ${this.colors.blue2} ${b + half * 0.45}%, ${this.colors.gray} ${b + half}%, ${this.colors.gray} 100% )`;

    }

    setGray(word) {
        word.style.background = "none";
        word.style.color = this.colors.gray;
        word.style.webkitTextFillColor = this.colors.gray;
    }

    setWhite(word) {
        // Words tagged `.transition-word--accent` (e.g. the final "HERE.") rest
        // on a light -> deep blue gradient; everything else ends solid white.
        if (word.classList.contains("transition-word--accent")) {
            word.style.color = "transparent";
            word.style.webkitTextFillColor = "transparent";
            word.style.background =
                `linear-gradient( 90deg, ${this.colors.blue1} 0%, ${this.colors.blue2} 100% )`;
            word.style.backgroundClip = "text";
            word.style.webkitBackgroundClip = "text";
            return;
        }
        word.style.background = "none";
        word.style.color = this.colors.white;
        word.style.webkitTextFillColor = this.colors.white;
    }

    setGradient(word, progress) {

        progress = this.clamp(progress);

        word.style.color = "transparent";
        word.style.webkitTextFillColor = "transparent";

        word.style.background = this.gradient(progress);
        word.style.backgroundRepeat = "no-repeat";
        // Full-width, no offset: the sweep direction lives entirely in the
        // gradient stops (see gradient()), so there is no background-position
        // trickery to accidentally reverse it.
        word.style.backgroundSize = "100% 100%";
        word.style.backgroundPosition = "0 0";
        word.style.backgroundClip = "text";
        word.style.webkitBackgroundClip = "text";

    }

    createScroll() {

        // Paint the initial (all-gray) state so nothing is pre-revealed on load.
        this.render(0);

        this.scroll = ScrollTrigger.create({

            trigger: this.trigger,
            // Non-pinned reveal (matches the reference): progress is mapped to the
            // heading travelling up through the viewport. It starts at 0 (word 1
            // gray) when the heading rises into view and finishes (all revealed)
            // as it reaches the upper third — so the sweep always begins at word 1
            // regardless of where the section sits on the page.
            start: "top 85%",
            end: "top 25%",
            scrub: true,
            invalidateOnRefresh: true,

            onUpdate: (self) => {
                this.render(self.progress);
            }

        });

        // ScrollTrigger measures start/end once, at creation. On Shopify the
        // layout is NOT final yet — web fonts swap in and section images load
        // after this runs, shifting the heading down. That leaves start/end
        // stale, so the sweep can be "already finished" before you scroll to the
        // section. Re-measure once everything has settled.
        this.scheduleRefresh();

    }

    scheduleRefresh() {

        // After the window fully loads (images decoded, layout final).
        if (document.readyState === "complete") {
            requestAnimationFrame(() => this.refresh());
        } else {
            window.addEventListener("load", () => this.refresh(), { once: true });
        }

        // After web fonts finish loading (custom Shopify fonts change text height
        // and therefore the heading's position).
        if (document.fonts && document.fonts.ready) {
            document.fonts.ready.then(() => this.refresh());
        }

    }

    render(progress) {

        const total = this.words.length;
        const overall = progress * total;

        this.words.forEach((word, index) => {

            let local = overall - index;

            local = this.ease(this.clamp(local));

            if (overall <= index) {
                this.setGray(word);
            }
            else if (overall >= index + 1) {
                this.setWhite(word);
            }
            else {
                this.setGradient(word, local);
            }

        });

    }

    ease(t) {
        return t * t * (3 - 2 * t);
    }

    refresh() {
        ScrollTrigger.refresh();
    }

    destroy() {
        if (this.scroll) {
            this.scroll.kill();
        }
    }

}

const premiumTransition = new PremiumWordTransition();

window.addEventListener("resize", () => {
    premiumTransition.refresh();
});

document.addEventListener("shopify:section:load", () => {
    ScrollTrigger.refresh();
});
